import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import InstallPrompt from './components/InstallPrompt';
import { OfflineProvider } from './components/ui/offline-indicator';
import AppUpdateNotification from './components/shared/AppUpdateNotification';
import { RefreshableSection } from './components/shared/SectionRefresh';
import GPSPermissionRequest from './components/shared/GPSPermissionRequest';
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmergencyReset from './pages/EmergencyReset';
import AdminDashboard from './pages/AdminDashboard';
import DriverDashboard from './pages/DriverDashboard';
import DriverSettings from './pages/DriverSettings';
import DriverExpenses from './pages/DriverExpenses';
import DriverPetrol from './pages/DriverPetrol';
import DriverCarWash from './pages/DriverCarWash';
import DriverRideView from './pages/DriverRideView';
import DriverAttendanceCalendar from './pages/DriverAttendanceCalendar';
import PartnerLogin from './pages/PartnerLogin';
import PartnerRegister from './pages/PartnerRegister';
import PartnerDashboard from './pages/PartnerDashboard';
import { 
  isPushSupported, 
  requestNotificationPermission, 
  subscribeToPush,
  registerPushServiceWorker 
} from './utils/pushNotifications';
import { initActivityTracking, isSessionValid, clearAuthData } from './utils/sessionManager';
import '@/App.css';

// Smart backend URL detection - prioritizes runtime domain detection for production
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // For custom production domains (like app.outsiderstravel.gr), use same origin
    // This ensures the app always talks to its own domain's backend
    if (!hostname.includes('preview.emergentagent.com') && 
        !hostname.includes('emergent.host') && 
        !hostname.includes('localhost') &&
        !hostname.includes('127.0.0.1')) {
      // Production deployment - use same origin
      return window.location.origin;
    }
  }
  
  // For Emergent preview environments or local development, use the env variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback to empty string (relative paths)
  return '';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Export for debugging purposes
export const getActiveBackendUrl = () => BACKEND_URL;

// Create axios instance with credentials for cookie-based auth
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true, // Important for sending/receiving cookies
});

// Normalize FastAPI/Pydantic 422 validation errors (which return `detail` as an array of
// objects) into a readable string. Applied globally so that every `toast.error(err.response.data.detail)`
// call site renders a string instead of trying to render an object and crashing React.
const normalizeErrorResponse = (error) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (first && typeof first === 'object') {
      const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : null;
      const msg = first.msg || 'Validation error';
      error.response.data.detail = field ? `${field}: ${msg}` : msg;
    } else {
      error.response.data.detail = String(first || 'Validation error');
    }
  } else if (detail && typeof detail === 'object') {
    error.response.data.detail = detail.msg || JSON.stringify(detail);
  }
  return error;
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Hard-logout when the backend reports the device session was revoked
    // by an admin (or by logout-all). Refresh won't recover this — clear
    // local state and send the user to the login screen.
    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    if (status === 401 && typeof detail === 'string' && detail.toLowerCase().includes('session revoked')) {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('remember_me');
        sessionStorage.removeItem('auth_token');
        // Flag for the LoginPage toast. Survives all redirect races (router
        // navigate, window.location.replace, refresh-token retry, etc.)
        sessionStorage.setItem('logout_reason', 'session_revoked');
      } catch {}
      const path = window.location.pathname;
      if (!path.includes('/login')) {
        window.location.replace('/login?reason=session_revoked');
      }
    }
    return Promise.reject(normalizeErrorResponse(error));
  }
);

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Export apiClient for use in other components
export { apiClient, API };

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Check both localStorage and sessionStorage for token
    return localStorage.getItem('token') || sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token');
  });
  const [loading, setLoading] = useState(true);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    const requestInterceptor = apiClient.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('token') || sessionStorage.getItem('auth_token');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            // Queue this request until refresh is done
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            }).catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            // Try to refresh the token
            const refreshResponse = await axios.post(
              `${API}/auth/refresh`,
              {},
              { withCredentials: true }
            );

            const { access_token, expires_in } = refreshResponse.data;
            
            // Store new token
            const rememberMe = localStorage.getItem('remember_me') === 'true';
            if (rememberMe) {
              localStorage.setItem('token', access_token);
            } else {
              sessionStorage.setItem('auth_token', access_token);
            }
            
            setToken(access_token);
            if (expires_in) {
              setTokenExpiresAt(Date.now() + expires_in * 1000);
            }

            processQueue(null, access_token);
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            
            // Refresh failed - logout user
            localStorage.removeItem('token');
            localStorage.removeItem('remember_me');
            sessionStorage.removeItem('auth_token');
            clearAuthData();
            setToken(null);
            setUser(null);

            // If the backend signalled the session was revoked, surface it on /login
            const detail = refreshError?.response?.data?.detail || '';
            if (typeof detail === 'string' && detail.toLowerCase().includes('session revoked')) {
              try { sessionStorage.setItem('logout_reason', 'session_revoked'); } catch {}
              const path = window.location.pathname;
              if (!path.includes('/login')) {
                window.location.replace('/login?reason=session_revoked');
              }
            }

            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(normalizeErrorResponse(error));
      }
    );

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    // Initialize activity tracking for session management
    const cleanupTracking = initActivityTracking();
    return cleanupTracking;
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          
          // Setup push notifications if user is already logged in
          setupPushNotifications(token);
        } catch (error) {
          console.error('Token verification failed:', error);
          
          // Only try to refresh if we got a 401 (unauthorized) - other errors might be network issues
          if (error.response?.status === 401) {
            try {
              const refreshResponse = await axios.post(
                `${API}/auth/refresh`,
                {},
                { withCredentials: true }
              );
              
              const { access_token, expires_in } = refreshResponse.data;
              const rememberMe = localStorage.getItem('remember_me') === 'true';
              
              if (rememberMe) {
                localStorage.setItem('token', access_token);
              } else {
                sessionStorage.setItem('auth_token', access_token);
              }
              
              setToken(access_token);
              if (expires_in) {
                setTokenExpiresAt(Date.now() + expires_in * 1000);
              }
              
              // Retry getting user info
              const userResponse = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${access_token}` }
              });
              setUser(userResponse.data);
              setupPushNotifications(access_token);
            } catch (refreshError) {
              // Refresh failed - clear auth data only if it's a 401 (token truly invalid)
              if (refreshError.response?.status === 401) {
                console.log('Refresh token invalid, clearing auth data');
                clearAuthData();
                localStorage.removeItem('token');
                localStorage.removeItem('remember_me');
                setToken(null);

                // Preserve "session revoked" reason in the redirect when the
                // backend explicitly says so (admin disconnect).
                const detail = refreshError?.response?.data?.detail || '';
                if (typeof detail === 'string' && detail.toLowerCase().includes('session revoked')) {
                  try { sessionStorage.setItem('logout_reason', 'session_revoked'); } catch {}
                  const path = window.location.pathname;
                  if (!path.includes('/login')) {
                    window.location.replace('/login?reason=session_revoked');
                  }
                }
              } else {
                // Network error or other issue - keep the token, user might come back online
                console.log('Network error during refresh, keeping token for offline access');
              }
            }
          } else {
            // Network error or server issue - don't clear the token, user might be offline
            console.log('Non-401 error during verification, keeping token');
          }
        }
      }
      setLoading(false);
    };
    
    const setupPushNotifications = async (authToken) => {
      if (!isPushSupported()) return;
      
      try {
        await registerPushServiceWorker();
        const permission = await requestNotificationPermission();
        if (permission === 'granted') {
          await subscribeToPush(authToken);
        }
      } catch (error) {
        console.error('Error setting up push notifications:', error);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (email, password, rememberMe = false, onRetry = null) => {
    // Retry logic for transient server errors (520, 502, 504, 524, timeouts).
    // Timeout raised from 10s to 25s so cold-starts don't burn all attempts,
    // and an exponential backoff (1.5s → 3s → 4.5s) between attempts.
    let lastError;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await axios.post(
          `${API}/auth/login`,
          { email, password },
          { withCredentials: true, timeout: 25000 }
        );
        const { access_token, user: userData, expires_in } = response.data;
        
        if (rememberMe) {
          localStorage.setItem('token', access_token);
          localStorage.setItem('remember_me', 'true');
        } else {
          sessionStorage.setItem('auth_token', access_token);
          localStorage.removeItem('remember_me');
        }
        
        setToken(access_token);
        setUser(userData);
        
        if (expires_in) {
          setTokenExpiresAt(Date.now() + expires_in * 1000);
        }
        
        setupPushNotifications(access_token);
        
        return userData;
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        const code = error.code;
        // Retry only on transient errors: no response (network), 5xx, 429, or axios timeout
        const isTransient = !status || status >= 500 || status === 429 || code === 'ECONNABORTED';
        if (!isTransient) throw error;
        if (attempt < 2) {
          if (typeof onRetry === 'function') {
            try { onRetry(attempt + 2); } catch { /* ignore */ }
          }
          await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        }
      }
    }
    throw lastError;
  };
  
  const setupPushNotifications = async (authToken) => {
    if (!isPushSupported()) {
      console.log('Push notifications not supported');
      return;
    }
    
    try {
      // Register service worker first
      await registerPushServiceWorker();
      
      // Request permission if not already granted
      const permission = await requestNotificationPermission();
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        await subscribeToPush(authToken);
        console.log('Push notifications enabled');
      } else {
        console.log('Push notification permission denied');
      }
    } catch (error) {
      console.error('Error setting up push notifications:', error);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to revoke tokens server-side
      await axios.post(
        `${API}/auth/logout`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        }
      );
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    
    // Clear all storage
    localStorage.removeItem('token');
    localStorage.removeItem('remember_me');
    sessionStorage.removeItem('auth_token');
    clearAuthData();
    setToken(null);
    setUser(null);
    setTokenExpiresAt(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };
  
  // Function to manually refresh token (can be called by components)
  const refreshToken = async () => {
    try {
      const response = await axios.post(
        `${API}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      
      const { access_token, expires_in } = response.data;
      const rememberMe = localStorage.getItem('remember_me') === 'true';
      
      if (rememberMe) {
        localStorage.setItem('token', access_token);
      } else {
        sessionStorage.setItem('auth_token', access_token);
      }
      
      setToken(access_token);
      if (expires_in) {
        setTokenExpiresAt(Date.now() + expires_in * 1000);
      }
      
      return access_token;
    } catch (error) {
      // If refresh fails, logout
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      updateUser, 
      loading, 
      refreshToken,
      tokenExpiresAt 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role access
  // admin-driver has access to both admin and driver routes
  const hasAccess = () => {
    if (!requiredRole) return true;
    if (user.role === requiredRole) return true;
    if (user.role === 'admin-driver') {
      return requiredRole === 'admin' || requiredRole === 'driver';
    }
    return false;
  };

  if (!hasAccess()) {
    // Redirect based on user role
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'driver') return <Navigate to="/driver" replace />;
    if (user.role === 'admin-driver') return <Navigate to="/admin" replace />;
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <OfflineProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/emergency-reset" element={<EmergencyReset />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverDashboard /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/settings"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverSettings /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/expenses"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverExpenses /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/petrol"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverPetrol /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/car-wash"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverCarWash /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/ride/:rideId"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverRideView /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/attendance"
              element={
                <ProtectedRoute requiredRole="driver">
                  <RefreshableSection><DriverAttendanceCalendar /></RefreshableSection>
                </ProtectedRoute>
              }
            />
            {/* Partner Portal Routes */}
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner/register" element={<PartnerRegister />} />
            <Route path="/partner" element={<RefreshableSection><PartnerDashboard /></RefreshableSection>} />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
          <InstallPrompt />
          <AppUpdateNotification />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </OfflineProvider>
  );
}

export default App;